/**
 * Manufacturing management → Assembly work instructions (revisioned, submit, approve).
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SaveChangesButton } from "@/components/ui/SaveChangesButton";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { formSnapshotDirty } from "@/lib/formDirty";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";

import {
  approveAssemblyWorkInstructionRevision,
  ASSEMBLY_WORK_INSTRUCTIONS_QUERY_KEY,
  createAssemblyWorkInstructionRevision,
  ensureAssemblyWorkInstruction,
  fetchAssemblyWorkInstructionPendingApprovals,
  fetchAssemblyWorkInstructionRevisions,
  fetchAssemblyWorkInstructions,
  submitAssemblyWorkInstructionRevision,
  updateAssemblyWorkInstructionRevision,
  type AssemblyWorkInstructionRevisionRow,
} from "./assemblyWorkInstructionApi";
import { AssemblyWorkInstructionStepsList } from "./AssemblyWorkInstructionStepsList";
import { fetchAssemblyKinds } from "./assemblyApi";

const PAGE_HELP =
  "Author numbered assembly work instructions per assembly kind. Draft revisions can be edited until submitted. " +
  "Submitted revisions follow the same submit → approve flow as BoM template revisions. Floor Assembly shows the latest approved revision.";

export function AssemblyWorkInstructionsPage(): JSX.Element {
  const hubWritesAllowed = useManufacturingScopeHubWrite();
  const queryClient = useQueryClient();

  const kindsQuery = useQuery({
    queryKey: ["manufacturing", "assembly", "kinds"],
    queryFn: fetchAssemblyKinds,
    staleTime: 60_000,
  });

  const kinds = kindsQuery.data?.kinds ?? [];
  const [assemblyKind, setAssemblyKind] = useState("");
  const [selectedRevision, setSelectedRevision] = useState<number | null>(null);
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState<string[]>([""]);
  const [instructionsBaseline, setInstructionsBaseline] = useState<string | null>(null);

  const instructionsQuery = useQuery({
    queryKey: ASSEMBLY_WORK_INSTRUCTIONS_QUERY_KEY,
    queryFn: fetchAssemblyWorkInstructions,
    staleTime: 15_000,
  });

  const instructionMeta = instructionsQuery.data?.instructions.find(
    (row) => row.assembly_kind === assemblyKind,
  );

  const pendingQuery = useQuery({
    queryKey: [...ASSEMBLY_WORK_INSTRUCTIONS_QUERY_KEY, "pending"],
    queryFn: fetchAssemblyWorkInstructionPendingApprovals,
    staleTime: 15_000,
  });

  useEffect(() => {
    if (assemblyKind === "" && kinds.length > 0) {
      setAssemblyKind(kinds[0]!.id);
    }
  }, [assemblyKind, kinds]);

  const revisionsQuery = useQuery({
    queryKey: [...ASSEMBLY_WORK_INSTRUCTIONS_QUERY_KEY, "revisions", assemblyKind],
    queryFn: () => fetchAssemblyWorkInstructionRevisions(assemblyKind),
    enabled: assemblyKind !== "",
    staleTime: 5_000,
  });

  const revisions = revisionsQuery.data?.revisions ?? [];
  const activeRevision = useMemo(
    () => revisions.find((row) => row.revision === selectedRevision) ?? revisions[0] ?? null,
    [revisions, selectedRevision],
  );

  useEffect(() => {
    if (activeRevision == null) {
      setSteps([""]);
      setSummary("");
      setDescription("");
      setInstructionsBaseline(null);
      return;
    }
    const nextSteps = activeRevision.steps.length > 0 ? activeRevision.steps : [""];
    const nextSummary = activeRevision.summary ?? "";
    const nextDescription = activeRevision.description ?? "";
    setSelectedRevision(activeRevision.revision);
    setSteps(nextSteps);
    setSummary(nextSummary);
    setDescription(nextDescription);
    setInstructionsBaseline(
      JSON.stringify({
        steps: nextSteps.map((step) => step.trim()),
        summary: nextSummary.trim(),
        description: nextDescription.trim(),
      }),
    );
  }, [activeRevision?.revision]);

  const revisionLocked = activeRevision?.approved_at != null || activeRevision?.submitted_at != null;
  const canEditDraft = hubWritesAllowed && activeRevision != null && !revisionLocked;
  const workInstructionsDirty =
    instructionsBaseline != null &&
    formSnapshotDirty(
      {
        steps: steps.map((step) => step.trim()),
        summary: summary.trim(),
        description: description.trim(),
      },
      JSON.parse(instructionsBaseline) as {
        steps: string[];
        summary: string;
        description: string;
      },
    );

  function invalidateAll(): void {
    void queryClient.invalidateQueries({ queryKey: ASSEMBLY_WORK_INSTRUCTIONS_QUERY_KEY });
    if (assemblyKind !== "") {
      void queryClient.invalidateQueries({
        queryKey: [...ASSEMBLY_WORK_INSTRUCTIONS_QUERY_KEY, "revisions", assemblyKind],
      });
    }
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const cleanedSteps = steps.map((step) => step.trim()).filter((step) => step !== "");
      if (assemblyKind === "" || cleanedSteps.length === 0) {
        throw new ApiError(400, "Select an assembly kind and enter at least one step.");
      }
      const kind = kinds.find((entry) => entry.id === assemblyKind);
      await ensureAssemblyWorkInstruction({ assemblyKind, title: kind?.label });
      if (activeRevision == null || revisionLocked) {
        return createAssemblyWorkInstructionRevision({
          assemblyKind,
          steps: cleanedSteps,
          summary,
          description,
        });
      }
      await updateAssemblyWorkInstructionRevision(assemblyKind, activeRevision.revision, {
        steps: cleanedSteps,
        summary,
        description,
      });
      return { revision: activeRevision.revision };
    },
    onSuccess: (result) => {
      setSelectedRevision(result.revision);
      setInstructionsBaseline(
        JSON.stringify({
          steps: steps.map((step) => step.trim()),
          summary: summary.trim(),
          description: description.trim(),
        }),
      );
      invalidateAll();
    },
  });

  const submitMutation = useMutation({
    mutationFn: () => {
      if (activeRevision == null) throw new ApiError(400, "Select a draft revision first.");
      return submitAssemblyWorkInstructionRevision({
        assemblyKind,
        revision: activeRevision.revision,
      });
    },
    onSuccess: invalidateAll,
  });

  const approveMutation = useMutation({
    mutationFn: (input: { assemblyKind: string; revision: number }) =>
      approveAssemblyWorkInstructionRevision(input),
    onSuccess: invalidateAll,
  });

  const newRevisionMutation = useMutation({
    mutationFn: async () => {
      const cleanedSteps = steps.map((step) => step.trim()).filter((step) => step !== "");
      if (assemblyKind === "" || cleanedSteps.length === 0) {
        throw new ApiError(400, "Enter at least one step before creating a new revision.");
      }
      const kind = kinds.find((entry) => entry.id === assemblyKind);
      await ensureAssemblyWorkInstruction({ assemblyKind, title: kind?.label });
      return createAssemblyWorkInstructionRevision({
        assemblyKind,
        steps: cleanedSteps,
        summary,
        description,
      });
    },
    onSuccess: (result) => {
      setSelectedRevision(result.revision);
      invalidateAll();
    },
  });

  return (
    <div className="space-y-8">
      <PageTitleWithHelp hoverHint={PAGE_HELP}>Assembly work instructions</PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Instruction set</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="assembly-instruction-kind">Assembly kind</Label>
              <select
                id="assembly-instruction-kind"
                className="mt-1.5 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                value={assemblyKind}
                onChange={(event) => {
                  setAssemblyKind(event.target.value);
                  setSelectedRevision(null);
                }}
              >
                {kinds.map((kind) => (
                  <option key={kind.id} value={kind.id}>
                    {kind.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="assembly-instruction-revision">Revision</Label>
              <select
                id="assembly-instruction-revision"
                className="mt-1.5 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                value={activeRevision?.revision ?? ""}
                onChange={(event) => setSelectedRevision(Number.parseInt(event.target.value, 10))}
                disabled={revisions.length === 0}
              >
                {revisions.length === 0 ? <option value="">No revisions yet</option> : null}
                {revisions.map((revision) => (
                  <option key={revision.revision} value={revision.revision}>
                    Revision {revision.revision}
                    {revision.approved_at != null ? " (approved)" : revision.submitted_at != null ? " (submitted)" : " (draft)"}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {instructionMeta ? (
            <p className="text-xs text-slate-500">
              Latest approved revision: {instructionMeta.latest_approved_revision ?? "—"}
              {instructionMeta.pending_revision != null ? ` · Pending: ${instructionMeta.pending_revision}` : ""}
            </p>
          ) : null}

          {activeRevision ? (
            <div className="flex flex-wrap gap-2 text-xs text-slate-600">
              {activeRevision.approved_at != null ? <Badge tone="success">Approved</Badge> : null}
              {activeRevision.submitted_at != null && activeRevision.approved_at == null ? (
                <Badge tone="warning">Submitted</Badge>
              ) : null}
              {activeRevision.submitted_at == null && activeRevision.approved_at == null ? (
                <Badge tone="neutral">Draft</Badge>
              ) : null}
            </div>
          ) : null}

          <div>
            <Label htmlFor="assembly-instruction-summary">Summary</Label>
            <Input
              id="assembly-instruction-summary"
              value={summary}
              onChange={(event) => setSummary(event.target.value)}
              disabled={!canEditDraft && activeRevision != null}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="assembly-instruction-description">Revision notes</Label>
            <TextArea
              id="assembly-instruction-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              disabled={!canEditDraft && activeRevision != null}
              className="mt-1.5"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Steps</Label>
            {steps.map((step, index) => (
              <div key={index} className="flex gap-2">
                <span className="pt-2 text-sm font-medium text-slate-500">{index + 1}.</span>
                <Input
                  value={step}
                  onChange={(event) => {
                    const next = [...steps];
                    next[index] = event.target.value;
                    setSteps(next);
                  }}
                  disabled={!canEditDraft && activeRevision != null}
                  placeholder="Direct, imperative step for the operator"
                />
                {canEditDraft ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setSteps(steps.filter((_, stepIndex) => stepIndex !== index))}
                    disabled={steps.length <= 1}
                    aria-label="Remove step"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </Button>
                ) : null}
              </div>
            ))}
            {canEditDraft ? (
              <Button type="button" size="sm" variant="secondary" onClick={() => setSteps([...steps, ""])}>
                <Plus className="h-4 w-4" aria-hidden />
                Add step
              </Button>
            ) : null}
          </div>

          {hubWritesAllowed ? (
            <div className="flex flex-wrap gap-2">
              {canEditDraft ? (
                <SaveChangesButton
                  type="button"
                  isDirty={workInstructionsDirty}
                  onClick={() => saveMutation.mutate()}
                  isLoading={saveMutation.isPending}
                >
                  Save draft
                </SaveChangesButton>
              ) : null}
              {activeRevision != null && activeRevision.submitted_at == null && activeRevision.approved_at == null ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => submitMutation.mutate()}
                  isLoading={submitMutation.isPending}
                >
                  Submit for approval
                </Button>
              ) : null}
              {activeRevision?.approved_at != null ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => newRevisionMutation.mutate()}
                  isLoading={newRevisionMutation.isPending}
                >
                  New revision from current steps
                </Button>
              ) : null}
              {revisions.length === 0 ? (
                <Button type="button" onClick={() => saveMutation.mutate()} isLoading={saveMutation.isPending}>
                  Create first revision
                </Button>
              ) : null}
            </div>
          ) : null}

          {saveMutation.isError || submitMutation.isError || newRevisionMutation.isError ? (
            <p className="text-sm text-rose-800">
              {((saveMutation.error ?? submitMutation.error ?? newRevisionMutation.error) as ApiError).message}
            </p>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pending approvals</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {pendingQuery.isLoading ? (
            <p className="text-sm text-slate-500">
              <Loader2 className="mr-1 inline h-4 w-4 animate-spin" aria-hidden /> Loading…
            </p>
          ) : (pendingQuery.data?.rows.length ?? 0) === 0 ? (
            <p className="text-sm text-slate-500">No revisions are waiting for approval.</p>
          ) : (
            <ul className="space-y-4">
              {pendingQuery.data?.rows.map((row) => (
                <PendingApprovalRow
                  key={`${row.assembly_kind}-${String(row.revision)}`}
                  row={row}
                  revision={
                    row.assembly_kind === assemblyKind
                      ? (revisions.find((entry) => entry.revision === row.revision) ?? null)
                      : null
                  }
                  hubWritesAllowed={hubWritesAllowed}
                  approvePending={approveMutation.isPending}
                  onApprove={() =>
                    approveMutation.mutate({ assemblyKind: row.assembly_kind, revision: row.revision })
                  }
                />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function PendingApprovalRow({
  row,
  revision,
  hubWritesAllowed,
  approvePending,
  onApprove,
}: {
  row: {
    assembly_kind: string;
    title: string;
    revision: number;
    summary: string | null;
    submitted_by: string | null;
    submitted_at: string | null;
  };
  revision: AssemblyWorkInstructionRevisionRow | null;
  hubWritesAllowed: boolean;
  approvePending: boolean;
  onApprove: () => void;
}): JSX.Element {
  return (
    <li className="rounded-md border border-slate-200 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-medium text-slate-900">
            {row.title} · revision {row.revision}
          </p>
          {row.summary ? <p className="mt-1 text-sm text-slate-600">{row.summary}</p> : null}
          <p className="mt-1 text-xs text-slate-500">
            Submitted {row.submitted_at ?? "—"}
            {row.submitted_by ? ` by ${row.submitted_by}` : ""}
          </p>
        </div>
        {hubWritesAllowed ? (
          <Button type="button" size="sm" onClick={onApprove} isLoading={approvePending}>
            Approve
          </Button>
        ) : null}
      </div>
      {revision ? (
        <div className="mt-3">
          <AssemblyWorkInstructionStepsList steps={revision.steps} />
        </div>
      ) : null}
    </li>
  );
}
